import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, LogOut, Shield, Users, Activity } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { formatApiError } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_admin_token")}` },
  withCredentials: true,
});

export default function AdminDashboard() {
  const [admin, setAdmin] = useState(null);
  const [perms, setPerms] = useState([]);
  const [role, setRole] = useState("");
  const [data, setData] = useState({ tickets: [], logs: [], sessions: [] });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("funz_admin_token");
    if (!token) { navigate("/admin"); return; }
    axios.get(`${API}/admin/enterprise/auth/me`, headers()).then((r) => {
      setAdmin(r.data.admin);
      setPerms(r.data.permissions);
      setRole(r.data.role_name);
    }).catch(() => { localStorage.removeItem("funz_admin_token"); navigate("/admin"); });
    axios.get(`${API}/admin/enterprise/tickets`, headers()).then((r) => setData((d) => ({ ...d, tickets: r.data.tickets }))).catch(() => {});
    axios.get(`${API}/admin/enterprise/activity-logs?limit=20`, headers()).then((r) => setData((d) => ({ ...d, logs: r.data.logs }))).catch(() => {});
    axios.get(`${API}/admin/enterprise/sessions`, headers()).then((r) => setData((d) => ({ ...d, sessions: r.data.sessions }))).catch(() => {});
  }, []);

  const logout = () => {
    localStorage.removeItem("funz_admin_token");
    localStorage.removeItem("funz_admin");
    navigate("/admin");
  };

  if (!admin) return <LoaderCircle className="mx-auto mt-20 h-8 w-8 animate-spin text-[#00D4FF]" />;

  return (
    <div data-testid="admin-dashboard" className="grid-bg min-h-screen bg-[var(--bg-base)] text-[var(--text-primary)]">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[var(--bg-base)]/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-display text-base font-extrabold">FUN<span className="grad-text">Z</span> Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-[#00D4FF]/10 px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider text-[#00D4FF]">{role || "Admin"}</span>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-slate-400 hover:text-white">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="glass rounded-3xl p-6">
          <p className="font-display text-2xl font-extrabold">Welcome, {admin.full_name}</p>
          <p className="mt-1 font-jbmono text-xs text-slate-500">{admin.employee_code} · {admin.email} · {admin.status}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {perms.map((p) => (
              <span key={p} className="rounded-full border border-[#7B61FF]/30 bg-[#7B61FF]/10 px-3 py-1 font-jbmono text-[9px] font-bold text-[#A78BFA]">{p}</span>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="glass rounded-2xl p-5 text-center">
            <p className="font-display text-3xl font-extrabold">{data.tickets.filter((t) => t.status === "OPEN").length}</p>
            <p className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-500">Open tickets</p>
          </div>
          <div className="glass rounded-2xl p-5 text-center">
            <p className="font-display text-3xl font-extrabold">{data.sessions.length}</p>
            <p className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-500">Sessions</p>
          </div>
          <div className="glass rounded-2xl p-5 text-center">
            <p className="font-display text-3xl font-extrabold">{data.logs.length}</p>
            <p className="mt-1 text-xs font-mono uppercase tracking-widest text-slate-500">Audit entries</p>
          </div>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <div className="glass rounded-3xl p-6">
            <p className="flex items-center gap-2 font-display text-lg font-bold"><Users className="h-5 w-5 text-[#00D4FF]" /> Support Tickets</p>
            <div className="mt-4 space-y-2">
              {data.tickets.slice(0, 5).map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#09090B]/80 p-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-bold">{t.subject}</p>
                    <p className="font-jbmono text-[10px] text-slate-500">{t.priority} · {t.status}</p>
                  </div>
                  {t.sla_breached && <span className="rounded-full bg-[#EF4444]/10 px-2.5 py-1 font-jbmono text-[9px] font-bold text-[#EF4444]">SLA</span>}
                </div>
              ))}
            </div>
          </div>
          <div className="glass rounded-3xl p-6">
            <p className="flex items-center gap-2 font-display text-lg font-bold"><Activity className="h-5 w-5 text-[#7B61FF]" /> Recent Activity</p>
            <div className="mt-4 space-y-1.5">
              {data.logs.slice(0, 8).map((l) => (
                <div key={l.id} className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#09090B]/60 px-3.5 py-2">
                  <span className="rounded-full bg-[#7B61FF]/10 px-2.5 py-0.5 font-jbmono text-[9px] font-bold text-[#A78BFA]">{l.action_type}</span>
                  <span className="min-w-0 flex-1 truncate font-jbmono text-[10px] text-slate-500">{l.module_name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
